'use client'

import { ChangeEvent, useRef } from 'react'
import { Button, Icon } from '@vinted/web-ui'
import { Camera24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  AcceptedFileTypes,
  MAXIMUM_FILE_SIZE_BYTES,
} from '@marketplace-web/item-upload/media-upload-feature'
import { MediaUploadPhotoDto, createPhoto } from '@marketplace-web/item-upload/media-upload-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import {
  conversationUploadPhotoIconClick,
  Conversation,
} from '@marketplace-web/messaging/conversation-messaging-data'

type Props = {
  conversation?: Conversation
  storeImage: (image: MediaUploadPhotoDto) => void
  onError?: (element: string | JSX.Element) => void
}

const Upload = ({ conversation, storeImage, onError }: Props) => {
  const translate = useTranslate('conversation.composer')
  const fileInput = useRef<HTMLInputElement>(null)
  const { track } = useTracking()

  const handleClick = () => {
    fileInput.current?.click()

    track(
      conversationUploadPhotoIconClick({
        transactionId: String(conversation?.transactionId),
        conversationId: String(conversation?.id),
        userSide: conversation?.transaction?.userSide ?? null,
        oppositeUserId: conversation?.oppositeUser?.id ?? null,
      }),
    )
  }

  const createImage = async (file: File) => {
    const type = 'user_msg'
    const photo = new FormData()

    if (file.size > MAXIMUM_FILE_SIZE_BYTES) {
      if (onError) onError(translate('errors.upload.file_size'))

      return
    }

    photo.append('photo[type]', type)
    photo.append('photo[file]', file)

    const response = await createPhoto(photo)

    if ('errors' in response) return

    storeImage(response)
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const filesFromEvent = event.target.files

    if (!filesFromEvent) return

    const files = Array.from(filesFromEvent)

    files.forEach(createImage)
  }

  return (
    <>
      <input
        ref={fileInput}
        onChange={onChange}
        hidden
        type="file"
        accept={AcceptedFileTypes.Images}
        multiple
      />
      <Button
        inline
        styling={Button.Styling.Flat}
        size={Button.Size.Small}
        theme="muted"
        icon={<Icon name={Camera24} testId="camera" color={Icon.Color.GreyscaleLevel3} />}
        onClick={handleClick}
        aria={{
          'aria-label': translate('a11y.actions.upload'),
        }}
      />
    </>
  )
}

export default Upload
